/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { Component, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { PageSEOProps, SupportedLanguage } from './seo.types';
import { seoConfig, getFullTitle, getCanonicalUrl, getOGLocale } from './seo-config';

/**
 * Isolates `react-helmet-async` render failures so a broken SEO head never
 * crashes the surrounding page; renders `fallback` instead when it catches.
 */
class HelmetErrorBoundary extends Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  /**
   * React error-boundary hook: flips to the error state on a child throw.
   *
   * @returns The next state marking the boundary as errored.
   */
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  /**
   * @returns The fallback node when errored, otherwise the wrapped children.
   */
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/**
 * Declarative per-page SEO head (title, description, canonical, Open Graph,
 * Twitter, and extra meta/links). Also syncs `document.title` imperatively and
 * shields the app from Helmet render errors.
 *
 * @param props SEO fields; see {@link PageSEOProps}.
 * @returns The error-boundary-wrapped Helmet head content.
 */
export function PageSEO({
  title,
  description,
  canonicalUrl,
  noIndex = false,
  noFollow = false,
  language,
  openGraph,
  twitter,
  additionalMeta = [],
  additionalLinks = [],
}: PageSEOProps) {
  const fullTitle = getFullTitle(title);

  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  return (
    <HelmetErrorBoundary fallback={null}>
      <HelmetContent
        title={title}
        fullTitle={fullTitle}
        description={description}
        canonicalUrl={canonicalUrl}
        noIndex={noIndex}
        noFollow={noFollow}
        language={language}
        openGraph={openGraph}
        twitter={twitter}
        additionalMeta={additionalMeta}
        additionalLinks={additionalLinks}
      />
    </HelmetErrorBoundary>
  );
}

/**
 * Renders the actual Helmet tags. Kept separate from {@link PageSEO} so it runs
 * inside the error boundary and can use router/i18n hooks to resolve locale and
 * canonical URL defaults.
 *
 * @param props SEO fields plus the precomputed `fullTitle`.
 * @returns The `<Helmet>` element with all resolved SEO tags.
 */
function HelmetContent({
  title,
  fullTitle,
  description,
  canonicalUrl,
  noIndex,
  noFollow,
  language,
  openGraph,
  twitter,
  additionalMeta = [],
  additionalLinks = [],
}: PageSEOProps & { fullTitle: string }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  
  const currentLanguage = (language || i18n.language || seoConfig.defaultLanguage) as SupportedLanguage;
  const canonical = canonicalUrl || getCanonicalUrl(location.pathname);
  const resolvedDescription = description ?? seoConfig.defaultDescription;
  
  const robotsContent = [
    noIndex ? 'noindex' : 'index',
    noFollow ? 'nofollow' : 'follow',
    'max-snippet:-1',
    'max-image-preview:large',
    'max-video-preview:-1',
  ].join(', ');
  
  const ogTitle = openGraph?.title || title;
  const ogDescription = openGraph?.description || resolvedDescription;
  const ogImage = openGraph?.image || seoConfig.defaultImage;
  const ogUrl = openGraph?.url || canonical;
  const ogType = openGraph?.type || 'website';
  const ogLocale = getOGLocale(currentLanguage);
  
  const twitterTitle = twitter?.title || title;
  const twitterDescription = twitter?.description || resolvedDescription;
  const twitterImage = twitter?.image || ogImage;
  const twitterCard = twitter?.cardType || 'summary_large_image';
  
  return (
    <Helmet>
      <html lang={currentLanguage} />
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robotsContent} />
      
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImage} />
      {openGraph?.imageAlt && <meta property="og:image:alt" content={openGraph.imageAlt} />}
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content={openGraph?.siteName || seoConfig.siteName} />
      
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={twitterImage} />
      {twitter?.imageAlt && <meta name="twitter:image:alt" content={twitter.imageAlt} />}
      {twitter?.site && <meta name="twitter:site" content={twitter.site} />}
      {twitter?.creator && <meta name="twitter:creator" content={twitter.creator} />}
      
      {additionalMeta.map((meta, index) => (
        <meta
          key={`meta-${index}`}
          {...(meta.name ? { name: meta.name } : {})}
          {...(meta.property ? { property: meta.property } : {})}
          content={meta.content}
        />
      ))}
      
      {additionalLinks.map((link, index) => (
        <link
          key={`link-${index}`}
          rel={link.rel}
          href={link.href}
          {...(link.hreflang ? { hrefLang: link.hreflang } : {})}
        />
      ))}
    </Helmet>
  );
}

export default PageSEO;
